import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  LinearProgress,
  Chip,
  Card,
  CardContent,
} from '@material-ui/core';
import {
  CheckCircle,
  Warning,
  Help,
  Security,
  Visibility,
  TouchApp,
} from '@material-ui/icons';
import { makeStyles, useTheme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    borderRadius: 16,
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  chartContainer: {
    position: 'relative',
    height: 300,
    marginBottom: theme.spacing(2),
  },
  pieChart: {
    position: 'relative',
    width: 250,
    height: 250,
    margin: '0 auto',
  },
  chartCenter: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    zIndex: 2,
  },
  statCard: {
    borderRadius: 12,
    padding: theme.spacing(2),
    height: '100%',
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.8)',
    border: '1px solid rgba(255,255,255,0.3)',
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },
  metricRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 8,
    background: 'rgba(255,255,255,0.5)',
  },
  progressContainer: {
    marginTop: theme.spacing(1),
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginBottom: theme.spacing(0.5),
  },
  clickRate: {
    fontSize: '0.75rem',
    opacity: 0.8,
  },
}));

const LinkSafetyAnalytics = ({
  scannedLinks = { clicked: 0, seen: 0 },
  unknownLinks = { clicked: 0, seen: 0 },
  unsafeLinks = { clicked: 0, seen: 0 },
  getTranslation = (text) => text,
  preferredLanguage = 'EN',
}) => {
  const classes = useStyles();
  const theme = useTheme();

  // Calculate safe links
  const safeLinks = useMemo(() => {
    const safeSeen = scannedLinks.seen - (unknownLinks.seen + unsafeLinks.seen);
    const safeClicked = scannedLinks.clicked - (unknownLinks.clicked + unsafeLinks.clicked);
    return {
      clicked: Math.max(0, safeClicked), // Ensure non-negative
      seen: Math.max(0, safeSeen)
    };
  }, [scannedLinks, unknownLinks, unsafeLinks]);

  // Calculate percentages and metrics
  const metrics = useMemo(() => {
    const totalSeen = scannedLinks.seen;
    const totalClicked = scannedLinks.clicked;

    if (totalSeen === 0) {
      return {
        safePercentage: 0,
        unknownPercentage: 0,
        unsafePercentage: 0,
        overallClickRate: 0,
        safeClickRate: 0,
        unknownClickRate: 0,
        unsafeClickRate: 0,
      };
    }

    const safePercentage = (safeLinks.seen / totalSeen) * 100;
    const unknownPercentage = (unknownLinks.seen / totalSeen) * 100;
    const unsafePercentage = (unsafeLinks.seen / totalSeen) * 100;

    const overallClickRate = (totalClicked / totalSeen) * 100;
    const safeClickRate = safeLinks.seen > 0 ? (safeLinks.clicked / safeLinks.seen) * 100 : 0;
    const unknownClickRate = unknownLinks.seen > 0 ? (unknownLinks.clicked / unknownLinks.seen) * 100 : 0;
    const unsafeClickRate = unsafeLinks.seen > 0 ? (unsafeLinks.clicked / unsafeLinks.seen) * 100 : 0;

    return {
      safePercentage,
      unknownPercentage,
      unsafePercentage,
      overallClickRate,
      safeClickRate,
      unknownClickRate,
      unsafeClickRate,
    };
  }, [safeLinks, unknownLinks, unsafeLinks, scannedLinks]);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getSecurityStatus = () => {
    if (metrics.unsafePercentage > 1) return { label: getTranslation('High Risk', preferredLanguage), color: '#f44336' };
    if (metrics.unknownPercentage > 15) return { label: getTranslation('Moderate Risk', preferredLanguage), color: '#ffc107' };
    return { label: getTranslation('Good Security', preferredLanguage), color: '#4caf50' };
  };

  const securityStatus = getSecurityStatus();

  // Create pie chart segments
  const createPieChart = () => {
    const radius = 55;
    const centerX = 75;
    const centerY = 75;
    
    let currentAngle = 0;
    const segments = [];

    // Safe links (green)
    const safeAngle = (metrics.safePercentage / 100) * 360;
    if (safeAngle > 0) {
      const largeArcFlag = safeAngle > 180 ? 1 : 0;
      const endAngle = currentAngle + safeAngle;
      const x1 = centerX + radius * Math.cos((currentAngle * Math.PI) / 180);
      const y1 = centerY + radius * Math.sin((currentAngle * Math.PI) / 180);
      const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
      const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180);

      const pathData = [
        `M ${centerX} ${centerY}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z'
      ].join(' ');

      segments.push(
        <path key="safe" d={pathData} fill="#4caf50" opacity="0.8" />
      );
      currentAngle = endAngle;
    }

    // Unknown links (orange)
    const unknownAngle = (metrics.unknownPercentage / 100) * 360;
    if (unknownAngle > 0) {
      const largeArcFlag = unknownAngle > 180 ? 1 : 0;
      const endAngle = currentAngle + unknownAngle;
      const x1 = centerX + radius * Math.cos((currentAngle * Math.PI) / 180);
      const y1 = centerY + radius * Math.sin((currentAngle * Math.PI) / 180);
      const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
      const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180);

      const pathData = [
        `M ${centerX} ${centerY}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z'
      ].join(' ');

      segments.push(
        <path key="unknown" d={pathData} fill="#ffc107" opacity="0.8" />
      );
      currentAngle = endAngle;
    }

    // Unsafe links (red)
    const unsafeAngle = (metrics.unsafePercentage / 100) * 360;
    if (unsafeAngle > 0) {
      const largeArcFlag = unsafeAngle > 180 ? 1 : 0;
      const endAngle = currentAngle + unsafeAngle;
      const x1 = centerX + radius * Math.cos((currentAngle * Math.PI) / 180);
      const y1 = centerY + radius * Math.sin((currentAngle * Math.PI) / 180);
      const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
      const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180);

      const pathData = [
        `M ${centerX} ${centerY}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z'
      ].join(' ');

      segments.push(
        <path key="unsafe" d={pathData} fill="#f44336" opacity="0.8" />
      );
    }

    return segments;
  };

  return (
    <Card className={classes.container} style={{ height: '100%' }}>
      <CardContent>
        {/* Header */}
        <Box className={classes.header} style={{ marginBottom: 16 }}>
          <Security style={{ marginRight: 8, color: securityStatus.color }} />
          <Box>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
              {getTranslation('Link Safety Analytics', preferredLanguage)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {securityStatus.label} • {formatNumber(scannedLinks.seen)} {getTranslation('links scanned', preferredLanguage)}
            </Typography>
          </Box>
        </Box>

        {/* Compact Pie Chart */}
        <Box textAlign="center" marginBottom={2}>
          <Box position="relative" display="inline-block">
            <svg width="150" height="150" viewBox="0 0 150 150">
              {createPieChart()}
              <circle
                cx="75"
                cy="75"
                r="35"
                fill="white"
                stroke="#f0f0f0"
                strokeWidth="1"
              />
            </svg>
            <Box position="absolute" top="50%" left="50%" style={{ transform: 'translate(-50%, -50%)' }}>
              <Typography variant="h5" style={{ fontWeight: 700, color: securityStatus.color, marginBottom: -4, lineHeight: 1 }}>
                {Math.round(metrics.safePercentage)}%
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ lineHeight: 1 }}>
                {getTranslation('Safe', preferredLanguage)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Detailed Link Analysis */}
        <Box marginBottom={2}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
            {getTranslation('Link Classification Breakdown', preferredLanguage)}
          </Typography>
          
          {/* Safe Links */}
          <Box style={{ marginBottom: 12, padding: 8, borderRadius: 8, background: 'rgba(76, 175, 80, 0.1)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
              <Box display="flex" alignItems="center">
                <CheckCircle style={{ color: '#4caf50', fontSize: 16, marginRight: 4 }} />
                <Typography variant="body2" style={{ fontWeight: 600 }}>{getTranslation('Safe Links', preferredLanguage)}</Typography>
              </Box>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#4caf50' }}>
                {Math.round(metrics.safePercentage)}%
              </Typography>
            </Box>
            <Typography variant="caption" color="textSecondary">
              {formatNumber(safeLinks.seen)} {getTranslation('seen', preferredLanguage)} • {formatNumber(safeLinks.clicked)} {getTranslation('clicked', preferredLanguage)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={metrics.safePercentage}
              style={{
                height: 4,
                borderRadius: 2,
                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                marginTop: 4,
                '& .MuiLinearProgress-bar': { backgroundColor: '#4caf50' }
              }}
            />
          </Box>

          {/* Unknown Links */}
          <Box style={{ marginBottom: 12, padding: 8, borderRadius: 8, background: 'rgba(255, 193, 7, 0.1)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
              <Box display="flex" alignItems="center">
                <Help style={{ color: '#ffc107', fontSize: 16, marginRight: 4 }} />
                <Typography variant="body2" style={{ fontWeight: 600 }}>{getTranslation('Unknown Links', preferredLanguage)}</Typography>
              </Box>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#ffc107' }}>
                {Math.round(metrics.unknownPercentage)}%
              </Typography>
            </Box>
            <Typography variant="caption" color="textSecondary">
              {formatNumber(unknownLinks.seen)} {getTranslation('seen', preferredLanguage)} • {formatNumber(unknownLinks.clicked)} {getTranslation('clicked', preferredLanguage)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={metrics.unknownPercentage}
              style={{
                height: 4,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 193, 7, 0.2)',
                marginTop: 4,
                '& .MuiLinearProgress-bar': { backgroundColor: '#ffc107' }
              }}
            />
          </Box>

          {/* Unsafe Links */}
          <Box style={{ marginBottom: 12, padding: 8, borderRadius: 8, background: 'rgba(244, 67, 54, 0.1)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
              <Box display="flex" alignItems="center">
                <Warning style={{ color: '#f44336', fontSize: 16, marginRight: 4 }} />
                <Typography variant="body2" style={{ fontWeight: 600 }}>{getTranslation('Unsafe Links', preferredLanguage)}</Typography>
              </Box>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#f44336' }}>
                {Math.round(metrics.unsafePercentage)}%
              </Typography>
            </Box>
            <Typography variant="caption" color="textSecondary">
              {formatNumber(unsafeLinks.seen)} {getTranslation('seen', preferredLanguage)} • {formatNumber(unsafeLinks.clicked)} {getTranslation('clicked', preferredLanguage)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.max(metrics.unsafePercentage, 1)}
              style={{
                height: 4,
                borderRadius: 2,
                backgroundColor: 'rgba(244, 67, 54, 0.2)',
                marginTop: 4,
                '& .MuiLinearProgress-bar': { backgroundColor: '#f44336' }
              }}
            />
          </Box>
        </Box>

        {/* Summary Stats */}
        <Box style={{ 
          marginTop: 16, 
          padding: 12, 
          borderRadius: 8, 
          background: theme.palette.type === 'dark' ? theme.palette.background.default : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}>
          <Box display="flex" alignItems="center" marginBottom={1}>
            <Security style={{ color: securityStatus.color, fontSize: 16, marginRight: 4 }} />
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              {getTranslation('Overall Link Security', preferredLanguage)}: {securityStatus.label}
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="caption" color="textSecondary">{getTranslation('Click Rate', preferredLanguage)}</Typography>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#1976d2' }}>
                {metrics.overallClickRate.toFixed(1)}%
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="textSecondary">{getTranslation('Risk Exposure', preferredLanguage)}</Typography>
              <Typography variant="body2" style={{ fontWeight: 600, color: securityStatus.color }}>
                {(metrics.unknownPercentage + metrics.unsafePercentage).toFixed(1)}%
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="textSecondary">{getTranslation('Total Scanned', preferredLanguage)}</Typography>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#9c27b0' }}>
                {formatNumber(scannedLinks.seen)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LinkSafetyAnalytics;